import { computed, inject, Injectable, Signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { CONFIG_CONTEXT } from '../../../../enum/config-context.enum';
import { DiscountTypeEnum } from '../../../../enum/discount-type.enum';
import { TunnelDataStore } from '../../../../stores/tunnel/tunnel-data.store';
import { BathroomConfigurationData } from '../../../../stores/tunnel/model/configuration-data/bathroom-configuration-data';
import { BathroomConfigurationProductDto } from '../../../../models/configurators-2d/tunnel/bathroom/bathroom-tunnel-dtos';
import { ProductListItem } from '../../../../models/product-list/productListItem';
import { TunnelCartLine } from '../../common/purchase-list/models/tunnel-cart-line';
import { TunnelCartService } from '../../common/purchase-list/services/tunnel-cart.service';

/**
 * Liste d'achat du configurateur salle de bain. Les produits appartiennent à la configuration portée
 * par le {@link TunnelDataStore} ; les modifications ne sont enregistrées qu'au passage à l'étape
 * suivante.
 */
@Injectable({ providedIn: 'root' })
export class BathroomCartService extends TunnelCartService {
  private readonly tunnelDataStore = inject(TunnelDataStore);

  private readonly configurationData = toSignal(this.tunnelDataStore.getConfigurationData());
  private readonly currentDiscount = toSignal(this.tunnelDataStore.getCurrentDiscount());

  /** Configuration salle de bain courante. */
  private readonly configuration = computed(
    () => this.configurationData()?.get(CONFIG_CONTEXT.BATHROOM_V2) as BathroomConfigurationData
  );

  /** Produits de la configuration, triés par gamme. */
  readonly products = computed<BathroomConfigurationProductDto[]>(() =>
    [...(this.configuration()?.products ?? [])].sort((a, b) => BathroomCartService.compareByRange(a, b))
  );

  /** Pourcentage de remise appliqué à la configuration. */
  private readonly discountPercentage = computed(() => {
    switch (this.currentDiscount()) {
      case DiscountTypeEnum.Discount5:
        return 5;
      case DiscountTypeEnum.Discount10:
        return 10;
      case DiscountTypeEnum.Discount20:
        return 20;
      default:
        return 0;
    }
  });

  readonly lines: Signal<TunnelCartLine[]> = computed(() =>
    this.products().map(product => ({
      ean: product.ean,
      label: product.label,
      price: product.salePrice ?? 0,
      quantity: product.qty,
      imageSrc: product.imageSrc,
    }))
  );

  readonly total: Signal<number> = computed(() =>
    this.lines().reduce((total, line) => total + line.quantity * line.price, 0)
  );

  readonly totalWithDiscount: Signal<number> = computed(
    () => this.total() * (1 - this.discountPercentage() / 100)
  );

  readonly isLocked: Signal<boolean> = computed(() => this.configuration()?.isLocked ?? false);

  quantityOf(ean: string): number {
    return this.products().find(product => product.ean === ean)?.qty ?? 0;
  }

  setQuantity(ean: string, quantity: number): void {
    const configuration = this.configuration();
    if (!configuration) {
      return;
    }

    const products =
      quantity <= 0
        ? configuration.products.filter(product => product.ean !== ean)
        : configuration.products.map(product => (product.ean === ean ? { ...product, qty: quantity } : product));

    this.updateProducts(configuration, products);
  }

  addProduct(product: ProductListItem, quantity: number = 1): void {
    const configuration = this.configuration();
    if (!configuration) {
      return;
    }

    const currentProducts = configuration.products ?? [];
    const isAlreadyAdded = currentProducts.some(configurationProduct => configurationProduct.ean === product.ean);

    const products = isAlreadyAdded
      ? currentProducts.map(configurationProduct =>
          configurationProduct.ean === product.ean
            ? { ...configurationProduct, qty: configurationProduct.qty + quantity }
            : configurationProduct
        )
      : [...currentProducts, BathroomCartService.toConfigurationProduct(product, quantity)];

    this.updateProducts(configuration, products);
  }

  private updateProducts(
    configuration: BathroomConfigurationData,
    products: BathroomConfigurationProductDto[]
  ): void {
    this.tunnelDataStore.updateConfigurationData(CONFIG_CONTEXT.BATHROOM_V2, {
      ...configuration,
      products: products,
    } as BathroomConfigurationData);
  }

  private static toConfigurationProduct(product: ProductListItem, quantity: number): BathroomConfigurationProductDto {
    return {
      ean: product.ean,
      label: product.name,
      salePrice: product.price,
      qty: quantity,
      imageSrc: product.imgPaths?.[0],
      range: product.brand,
      isFromConfiguration: false,
    };
  }

  /** Ordonne les produits par gamme, ceux qui n'en ont pas venant en dernier. */
  private static compareByRange(a: BathroomConfigurationProductDto, b: BathroomConfigurationProductDto): number {
    const rangeA = a.range?.trim();
    const rangeB = b.range?.trim();

    if (!rangeA && !rangeB) return 0;
    if (!rangeA) return 1;
    if (!rangeB) return -1;

    return rangeA.localeCompare(rangeB);
  }
}
